/**
 * Blur gating, and the switch that decides whether it runs.
 *
 * Wired into the four capture modules, and off unless the client's config turns
 * it on -- see setBlurEnabled, which each of them calls from config["blurred"]
 * the same way the flash is switched on from config["torch"].
 *
 *   on    in the video modules, every candidate frame is analysed: a frame judged
 *         blurry is NOT uploaded, and while a region's frames are blurry its dial
 *         segment is held grey instead of going blue/green. That hold is one-way:
 *         once a segment has been drawn blue or green it is never taken back.
 *         StencilVideo checks nothing per frame and calls in only at the two
 *         moments a stencil changes colour, which are the two moments it keeps
 *         frames.
 *   off   nothing here runs and uploads are not gated -- the app behaves exactly
 *         as it did before this feature existed.
 *
 * The torch is a separate, independent switch: it reads brightness, this reads
 * focus, and both are served by one cached analysis of the frame, so running
 * them together costs no more than running either alone.
 *
 * Blurry frames are skipped and the next frame is tried -- a region can always
 * complete once the user holds steady, so there is no way to get stuck.
 *
 * Everything is recorded per region so the on-screen panel can show what was
 * judged and why. Never throws: a failed analysis is reported as "unknown" and
 * does not block an upload.
 */

import { analyzeImageQuality, boxesFromDetections } from "./imageQuality";

export const MODES = { TORCH: "torch", BLUR: "blur", NORMAL: "normal" };

// The order setMode validates against, and the order cycleMode/stepMode follow.
export const MODE_ORDER = [MODES.TORCH, MODES.BLUR, MODES.NORMAL];

// Blur verdict comes from analyzeImageQuality's own `blurry` flag, which is now
// a vote of the 4x4 grid: a frame is blurred when at least 7 of its 16 cells
// are. Keeping the decision there means the blur_score / tenengrad / cells
// columns always explain it.
const FRAME_LOG_SIZE = 8; // recent frames kept for the panel

// Periodic "is the lens actually dirty?" check, separate from the per-frame
// upload gate above. Individual blurry frames are normal -- the user moves, the
// camera hunts for focus -- and skipping them is enough. What this catches is
// the case a skip cannot fix: a smeared or fogged lens, where frame after frame
// comes back blurry and the inspection can never complete. The bar is every frame
// in the window, so one sharp frame is enough to say the lens is fine and stay out
// of the user's way.
//
// A full minute, and the clock starts on the first frame the model actually
// processed -- not when the component mounted. Camera warm-up, focus hunting and
// the first detections all happen before that first frame, and every one of them
// produces exactly the soft frames this is looking for. Starting the window
// earlier would mean the very first thing many users see is a popup telling them
// to clean a lens that is perfectly clean.
const BLUR_WINDOW_MS = 60000;
// Floor on sample size: a window that saw almost nothing (detection paused, user
// mid-transition) is not evidence of anything, whatever its ratio. Over a minute
// at roughly four frames a second a healthy window holds a couple of hundred
// frames, so twenty is a low bar that still rules out judging a whole minute on
// a handful of frames scraped from its last second.
const BLUR_WINDOW_MIN_FRAMES = 20;
// Share of the window that must be blurry. 1.0: EVERY frame in the window has to
// come back blurry before the user is interrupted. A single sharp frame anywhere
// in the minute proves the camera can still resolve the car, so whatever is wrong
// is not a lens that needs cleaning.
const BLUR_WINDOW_RATIO = 1.0;

// Off until a client's config asks for it. NORMAL rather than TORCH because the
// flash is no longer one of this switch's positions -- torchController owns that
// independently -- so the only question left here is gating on or off.
let mode = MODES.NORMAL;

let frames = []; // recent verdicts, newest last
let events = []; // mode switches and blocked uploads
let regionStats = {}; // region -> { seen, blurry, sharp }
let lastVerdict = null; // verdict for the most recent analysed frame
// Regions whose dial segment has already been drawn blue or green. Blur can hold
// a grey segment grey, but it can never take a colour back once the user has seen
// it, so these are exempt from masking for the rest of the inspection.
let revealed = {};
let blockedUploads = 0;
let analysing = false; // guards against overlapping analyses

// Rolling window state for the periodic check above.
let windowStart = 0; // epoch ms the current window opened, 0 = not started
let windowSeen = 0; // frames analysed in the current window
let windowBlurry = 0; // how many of those were blurry
let lastWindow = null; // the most recently closed window, kept for the popup/panel
let alertRaised = false; // latched once raised, until the user acknowledges

const hhmmss = () => new Date().toTimeString().slice(0, 8);

const pushEvent = (msg) => {
  events.push(`${hhmmss()} ${msg}`);
  if (events.length > 20) events.shift();
};

export const getMode = () => mode;
export const isTorchMode = () => mode === MODES.TORCH;
export const isBlurMode = () => mode === MODES.BLUR;
export const isNormalMode = () => mode === MODES.NORMAL;

/** Switch modes. Anything unrecognised falls back to torch. Returns the mode. */
export const setMode = (next) => {
  const wanted = MODE_ORDER.includes(next) ? next : MODES.TORCH;
  if (wanted === mode) return mode;
  const from = mode;
  mode = wanted;
  // A verdict from the previous mode must not gate the first frames of this one.
  lastVerdict = null;
  frames = [];
  pushEvent(`MODE ${from.toUpperCase()} -> ${mode.toUpperCase()}`);
  return mode;
};

/**
 * Turn blur gating on or off for this client, from config["blurred"].
 *
 * The flag has to be present AND true. Absent, or present and false, leaves
 * gating off, and everything else in here is then a no-op -- a client without
 * the flag pays nothing beyond a returned null.
 */
export const setBlurEnabled = (enabled) =>
  setMode(enabled ? MODES.BLUR : MODES.NORMAL);

/** Next mode in MODE_ORDER, wrapping. */
export const cycleMode = () =>
  setMode(MODE_ORDER[(MODE_ORDER.indexOf(mode) + 1) % MODE_ORDER.length]);

/**
 * Step one position through the swipe order. dir > 0 moves right, dir < 0 left.
 * Clamped rather than wrapped, so a drag cannot skip past the end and reappear at
 * the other side.
 */
export const stepMode = (dir) => {
  const i = MODE_ORDER.indexOf(mode);
  const next = Math.min(MODE_ORDER.length - 1, Math.max(0, i + (dir > 0 ? 1 : -1)));
  return setMode(MODE_ORDER[next]);
};

/** Clear everything for a new inspection. Leaves the chosen mode alone. */
export const resetBlur = () => {
  frames = [];
  events = [];
  regionStats = {};
  lastVerdict = null;
  revealed = {};
  blockedUploads = 0;
  analysing = false;
  windowStart = 0;
  windowSeen = 0;
  windowBlurry = 0;
  lastWindow = null;
  alertRaised = false;
};

const statsFor = (region) => {
  const key = region || "unknown";
  if (!regionStats[key]) regionStats[key] = { seen: 0, blurry: 0, sharp: 0 };
  return regionStats[key];
};

/** Fold one analysed frame into the open window. Opens one if none is running. */
const noteWindowFrame = (blurred) => {
  if (!windowStart) windowStart = Date.now();
  windowSeen += 1;
  if (blurred) windowBlurry += 1;
};

/**
 * Analyse one frame and record the verdict against its region.
 *
 * Runs on every frame while in blur mode -- not throttled like the torch check,
 * because the answer gates whether THIS frame may be uploaded, so a stale verdict
 * would gate the wrong frame. Overlapping calls are skipped rather than queued.
 *
 * Resolves { blurred, unknown, blurScore, tenengradScore }. `unknown` means the
 * analysis could not run, and the caller should let the frame through.
 */
export const checkFrameForBlur = async (
  base64,
  detections,
  videoW,
  videoH,
  region
) => {
  if (mode !== MODES.BLUR) return null;
  if (!base64 || !videoW || !videoH) return null;
  if (analysing) return lastVerdict; // reuse the newest verdict rather than queue
  analysing = true;

  try {
    // Same boxes as the torch check, so both read one analysis of the frame
    // rather than paying for it twice: the union of detected part boxes in
    // pixels, or the whole frame when nothing was detected, plus the lamps to
    // keep out of the glare score.
    const boxes = boxesFromDetections(detections, videoW, videoH);

    const q = await analyzeImageQuality(base64, boxes.car, {
      headlightBoxes: boxes.headlightBoxes,
      taillightBoxes: boxes.taillightBoxes,
    });
    const stats = statsFor(region);
    stats.seen += 1;
    if (q.blurry) stats.blurry += 1;
    else stats.sharp += 1;
    noteWindowFrame(!!q.blurry);

    lastVerdict = {
      blurred: !!q.blurry,
      unknown: false,
      blurScore: q.blurScore,
      tenengradScore: q.tenengradScore,
      // What the verdict was actually taken on, so a frame that was let through
      // and a frame that was blocked can be told apart by how close the cell
      // count came to the bar rather than by a single opaque boolean.
      blurryCells: q.blurryCells,
      gridCells: q.gridCells,
      glare: !!q.glare,
      region: region || "unknown",
      quality: q,
      t: Date.now(),
    };

    frames.push({
      blurred: lastVerdict.blurred,
      bs: q.blurScore,
      tg: q.tenengradScore,
      cells: q.blurryCells,
      total: q.gridCells,
      glare: !!q.glare,
      region: lastVerdict.region,
      t: hhmmss(),
    });
    if (frames.length > FRAME_LOG_SIZE) frames.shift();

    return lastVerdict;
  } catch (e) {
    // Never let a failed analysis block the inspection.
    lastVerdict = { blurred: false, unknown: true, region: region || "unknown" };
    return lastVerdict;
  } finally {
    analysing = false;
  }
};

/**
 * May the frame just analysed be uploaded?
 *
 * True while gating is off, true when the analysis could not run, and false only
 * when the last frame was positively judged blurry.
 */
export const mayUploadFrame = () => {
  if (mode !== MODES.BLUR) return true;
  if (!lastVerdict || lastVerdict.unknown) return true;
  return !lastVerdict.blurred;
};

/** Record that an upload was skipped, for the panel and the sheet. */
export const noteBlockedUpload = (region) => {
  blockedUploads += 1;
  pushEvent(`upload skipped (blurry) ${region || ""}`.trim());
};

export const getBlockedUploads = () => blockedUploads;
export const getLastVerdict = () => lastVerdict;

/**
 * Close the window if BLUR_WINDOW_MS has elapsed, and judge what it saw.
 *
 * Called from the detection loop on every analysed frame; returns null on all
 * the frames where the window is still open, so the caller can treat a non-null
 * result as "a window just closed". The counters reset either way -- a window
 * that closes under the sample floor simply starts the next one rather than
 * carrying its handful of frames forward.
 *
 * The alert is latched: once raised it stays raised, and no further window can
 * raise it again, until acknowledgeBlurAlert clears it. Without that a popup
 * dismissed while the lens is still dirty would re-fire on the very next window
 * and trap the user in a loop they cannot answer fast enough.
 */
export const takeBlurWindow = () => {
  try {
    if (mode !== MODES.BLUR) return null;
    if (!windowStart) return null;
    if (Date.now() - windowStart < BLUR_WINDOW_MS) return null;

    const seen = windowSeen;
    const blurry = windowBlurry;
    const ratio = seen > 0 ? blurry / seen : 0;
    const enough = seen >= BLUR_WINDOW_MIN_FRAMES;
    const blurred = enough && ratio >= BLUR_WINDOW_RATIO;

    lastWindow = {
      seen,
      blurry,
      sharp: seen - blurry,
      ratio,
      blurred,
      enough,
      at: Date.now(),
    };

    windowStart = Date.now();
    windowSeen = 0;
    windowBlurry = 0;

    pushEvent(
      `window ${blurry}/${seen} blurry` +
        (blurred ? " -> ALERT" : enough ? "" : " (too few frames)"),
    );

    // Report the raise only on the window that actually raises it.
    if (blurred && !alertRaised) {
      alertRaised = true;
      return lastWindow;
    }
    return null;
  } catch (e) {
    return null;
  }
};

/** Stats for the most recently closed window (what the popup shows). */
export const getLastBlurWindow = () => lastWindow;

export const isBlurAlertRaised = () => alertRaised;

/**
 * User has cleaned the lens and dismissed the popup. Clears the latch and opens
 * a fresh window, so the next judgement is made purely on frames captured after
 * the cleaning rather than on anything counted before it.
 */
export const acknowledgeBlurAlert = () => {
  alertRaised = false;
  windowStart = Date.now();
  windowSeen = 0;
  windowBlurry = 0;
  pushEvent("blur alert acknowledged - window restarted");
};

/**
 * Has every frame seen for this region been blurry?
 *
 * Only meaningful once at least one frame has been seen; a region nobody has
 * pointed the camera at yet is not "all blurry".
 */
export const isRegionAllBlurred = (region) => {
  const s = regionStats[region || "unknown"];
  return !!(s && s.seen > 0 && s.blurry === s.seen);
};

export const getRegionStats = (region) => {
  const s = regionStats[region || "unknown"];
  return s ? { ...s } : { seen: 0, blurry: 0, sharp: 0 };
};

export const getAllRegionStats = () => {
  const out = {};
  for (const k of Object.keys(regionStats)) out[k] = { ...regionStats[k] };
  return out;
};

/**
 * Should this region's segment be held grey on the strength of the last verdict?
 *
 * Only ever holds a segment that is still grey. A segment already drawn blue or
 * green is never taken back -- see `revealed`.
 */
const shouldHoldGrey = (regionsCovered, region) => {
  if (!region || !(region in regionsCovered)) return false;
  if (revealed[region]) return false;
  if (!lastVerdict || lastVerdict.unknown || !lastVerdict.blurred) return false;
  // The verdict must belong to THIS region. Without this check, moving to a new
  // region straight after a blurry frame would grey the new one on the strength
  // of the previous region's frame.
  return lastVerdict.region === region;
};

/**
 * A copy of the coverage map with the current region forced back to NOT_COVERED
 * while its frames are blurry, so the dial segment stays grey instead of turning
 * blue or green.
 *
 * One-way, and latched: this can only keep a segment from colouring in the first
 * place. Once a segment has actually been drawn blue or green it is recorded in
 * `revealed` and never masked again, so a blurry frame over a region the user has
 * already finished cannot take its colour away. Progress on the dial only ever
 * moves forwards, which is what the user is reading it for.
 *
 * A copy on purpose: the guidance module's own map is left untouched, so this only
 * changes what is drawn, never what the guidance believes. With gating off the map
 * is passed straight through.
 */
export const maskRegionsForBlur = (regionsCovered, region) => {
  if (mode !== MODES.BLUR) return regionsCovered;
  if (!regionsCovered || typeof regionsCovered !== "object") return regionsCovered;

  const masked = shouldHoldGrey(regionsCovered, region)
    ? { ...regionsCovered, [region]: 0 }
    : regionsCovered;

  // Latch on what is actually DRAWN, not on what guidance believes. On the frame
  // where a blurry region first turns covered, the mask above has already put it
  // back to grey, so it is not recorded here and the hold survives into the next
  // frame. It is recorded the first time it reaches the dial with a colour.
  for (const key of Object.keys(masked))
    if (masked[key] !== 0) revealed[key] = true;

  return masked;
};

/** Per-frame fields for logging, while gating is on. */
export const getSheetFields = (region) => {
  const stats = getRegionStats(region);
  return {
    mode,
    blurred: lastVerdict ? !!lastVerdict.blurred : null,
    blurryCells: lastVerdict ? lastVerdict.blurryCells : null,
    gridCells: lastVerdict ? lastVerdict.gridCells : null,
    glare: lastVerdict ? !!lastVerdict.glare : null,
    uploadBlocked: mode === MODES.BLUR && !mayUploadFrame(),
    region: region || "",
    regionSeen: stats.seen,
    regionBlurry: stats.blurry,
    regionAllBlur: isRegionAllBlurred(region),
  };
};

/** HTML for the on-screen panel: mode, the last frames, and per-region tallies. */
export const getBlurDebugHtml = () => {
  try {
    if (mode === MODES.NORMAL)
      return "<b>NORMAL MODE</b> &nbsp; <i>no torch, no blur gating</i>";
    if (mode !== MODES.BLUR)
      return "<b>torch mode</b> &nbsp; <i>blur checks idle</i>";
    const modeLabel = '<span style="color:#ffab40"><b>BLUR MODE</b></span>';

    const rows = frames
      .map(
        (f, i) =>
          `${i + 1}. ${f.blurred ? "BLUR" : "ok  "} ${f.cells}/${f.total} cells ` +
          `bs=${f.bs} tg=${f.tg}${f.glare ? " GLARE" : ""} ${f.region}`
      )
      .join("<br/>");

    const regions = Object.keys(regionStats)
      .map((r) => {
        const s = regionStats[r];
        const all = s.seen > 0 && s.blurry === s.seen ? " ALL-BLUR" : "";
        return `${r}: ${s.blurry}/${s.seen} blurry${all}`;
      })
      .join("<br/>");

    return (
      `${modeLabel} &nbsp; <b>skipped ${blockedUploads}</b><br/>` +
      `<b>last ${frames.length} frames:</b><br/>${rows || "-"}<br/>` +
      `<b>per region:</b><br/>${regions || "-"}<br/>` +
      `<b>events:</b><br/>${events.slice(-5).join("<br/>") || "-"}`
    );
  } catch (e) {
    return "";
  }
};
